import { useState } from 'react';
import {
  ChevronLeft,
  HelpCircle,
  MoreVertical,
  Search,
  Plus,
  ChevronDown,
} from 'lucide-react';
import { useSkills, type ApiSkill } from '../../lib/skills.queries';
import SkillEditor from './SkillEditor';

interface Props {
  onBack?: () => void;
  onSelectSkill?: (id: string) => void;
  onSettingsClick?: () => void;
}

const STATUS_OPTIONS = ['All', 'Published', 'Draft'] as const;
type StatusFilter = (typeof STATUS_OPTIONS)[number];

export function SkillList({ onBack, onSelectSkill, onSettingsClick }: Props) {
  const { data: skills = [], isLoading } = useSkills();
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [view, setView] = useState<'list' | 'editor'>('list');
  const [selectedSkillId, setSelectedSkillId] = useState<string | undefined>(
    undefined,
  );

  function openNewSkill() {
    setSelectedSkillId(undefined);
    setView('editor');
  }

  function openSkill(id: string) {
    onSelectSkill?.(id);
    setSelectedSkillId(id);
    setView('editor');
  }

  if (isLoading) {
    return <p className="p-6 text-center text-sm text-neutral-400">Loading…</p>;
  }

  if (view === 'editor') {
    return (
      <SkillEditor
        skillId={selectedSkillId}
        onBack={() => setView('list')}
        onSettingsClick={onSettingsClick}
      />
    );
  }

  const filtered = skills.filter((s) => {
    const matchesQuery =
      s.name.toLowerCase().includes(query.toLowerCase()) ||
      s.description.toLowerCase().includes(query.toLowerCase());
    const matchesStatus =
      statusFilter === 'All' || s.status === statusFilter.toLowerCase();
    return matchesQuery && matchesStatus;
  });

  const published = filtered.filter((s) => s.status === 'published');
  const drafts = filtered.filter((s) => s.status === 'draft');

  return (
    <div className="flex h-full flex-col bg-white">
      {/* Header (shared across mobile + desktop) */}
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3">
        <button
          onClick={onBack}
          className="text-ink hover:bg-ink/8 -ml-1 rounded p-1"
          aria-label="Go back"
        >
          <ChevronLeft size={22} strokeWidth={2} />
        </button>

        <div className="flex-1">
          <p className="text-ink text-lg leading-tight font-bold">Skills</p>
          <button
            onClick={onSettingsClick}
            className="text-xs text-neutral-500 hover:underline"
          >
            Settings
          </button>
        </div>

        <button
          className="text-ink hover:bg-ink/8 rounded-full p-1"
          aria-label="Help"
        >
          <HelpCircle size={20} strokeWidth={1.5} />
        </button>
        <button
          className="text-ink hover:bg-ink/8 rounded p-1"
          aria-label="More options"
        >
          <MoreVertical size={20} strokeWidth={1.5} />
        </button>
      </div>

      {/* Search */}
      <div className="px-4 pt-4 pb-3 lg:hidden">
        <div className="flex items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2.5">
          <Search size={16} className="shrink-0 text-neutral-400" />
          <input
            type="search"
            placeholder="Search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="text-ink flex-1 bg-transparent text-sm placeholder-neutral-400 outline-none"
          />
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 lg:hidden">
        <button
          onClick={openNewSkill}
          className="bg-danger flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 active:bg-red-800"
        >
          <Plus size={16} strokeWidth={2.5} />
          New skill
        </button>

        <StatusDropdown value={statusFilter} onChange={setStatusFilter} />
      </div>

      {/* List */}
      <div className="flex-1 space-y-4 overflow-y-auto px-4 pb-6 lg:hidden">
        {published.length > 0 && (
          <section className="my-10">
            <p className="text-ink mb-2 text-sm">Published</p>
            <div className="bg-ink/8 divide-ink/12 divide-y overflow-hidden rounded-md">
              {published.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onClick={() => openSkill(skill.id)}
                />
              ))}
            </div>
          </section>
        )}

        {drafts.length > 0 && (
          <section>
            <p className="text-ink mb-2 text-sm">Draft</p>
            <div className="divide-ink/12 divide-y overflow-hidden rounded-md bg-neutral-100">
              {drafts.map((skill) => (
                <SkillCard
                  key={skill.id}
                  skill={skill}
                  onClick={() => openSkill(skill.id)}
                />
              ))}
            </div>
          </section>
        )}

        {filtered.length === 0 && (
          <p className="pt-10 text-center text-sm text-neutral-400">
            No skills found
          </p>
        )}
      </div>

      {/* ── Desktop layout ─────────────────────────────────────────────── */}
      <div className="mt-30 hidden min-h-0 flex-1 flex-col p-6 lg:flex">
        {/* Toolbar: search + actions on one row */}
        <div className="mb-6 flex items-center gap-4">
          <div className="flex w-full max-w-md items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 py-2.5">
            <Search size={16} className="shrink-0 text-neutral-400" />
            <input
              type="search"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="text-ink flex-1 bg-transparent text-sm placeholder-neutral-400 outline-none"
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <button
              onClick={openNewSkill}
              className="bg-danger flex items-center gap-1.5 rounded-md px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 active:bg-red-800"
            >
              <Plus size={16} strokeWidth={2.5} />
              New skill
            </button>
            <StatusDropdown value={statusFilter} onChange={setStatusFilter} />
          </div>
        </div>

        {/* Table */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="border-b border-neutral-200">
                <th className="text-ink px-4 py-3 text-sm font-semibold">
                  Name
                </th>
                <th className="text-ink px-4 py-3 text-sm font-semibold">
                  Description
                </th>
                <th className="text-ink px-4 py-3 text-sm font-semibold">
                  Last Edited
                </th>
                <th className="text-ink px-4 py-3 text-sm font-semibold">
                  Status
                </th>
                <th className="text-ink px-4 py-3 text-sm font-semibold">
                  Author
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((skill) => (
                <tr
                  key={skill.id}
                  onClick={() => openSkill(skill.id)}
                  className="cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                >
                  <td className="text-ink px-4 py-3 text-sm font-medium">
                    {skill.name}
                  </td>
                  <td className="max-w-xs truncate px-4 py-3 text-sm text-neutral-600">
                    {skill.description}
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-600">
                    {new Date(skill.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <StatusBadge status={skill.status} />
                  </td>
                  <td className="px-4 py-3 text-sm text-neutral-400">—</td>
                </tr>
              ))}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <p className="pt-10 text-center text-sm text-neutral-400">
              No skills found
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusDropdown({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-ink flex items-center gap-1.5 rounded-md border border-neutral-300 px-4 py-2 text-sm hover:bg-neutral-50"
      >
        Status
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-36 overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className={[
                'w-full px-4 py-2 text-left text-sm hover:bg-neutral-50',
                value === opt ? 'text-ink font-semibold' : 'text-neutral-600',
              ].join(' ')}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: ApiSkill['status'] }) {
  const styles: Record<ApiSkill['status'], string> = {
    published:
      'bg-green-100 text-green-700 dark:bg-green-950/50 dark:text-green-300',
    draft: 'bg-neutral-100 text-neutral-600',
    archived: 'bg-neutral-100 text-neutral-500',
  };
  return (
    <span
      className={[
        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        styles[status],
      ].join(' ')}
    >
      {status}
    </span>
  );
}

function SkillCard({
  skill,
  onClick,
}: {
  skill: ApiSkill;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="w-full px-4 py-3 text-left transition-colors hover:bg-neutral-200/60 active:bg-neutral-200"
    >
      <p className="text-ink text-base font-bold">{skill.name}</p>
      <p className="text-sm text-neutral-600">{skill.description}</p>
      <p className="mt-2 text-xs text-neutral-400">
        {new Date(skill.updatedAt).toLocaleDateString()}
      </p>
    </button>
  );
}
